import type { Boat } from '@/modules/inventory/types/boat.types';
import {
  WIZARD_DEFAULT_VALUES,
  type WizardFormData,
} from '../types/wizard.types';

/**
 * Draft-resume utilities formerly used by the removed `AddBoatWizard` to hydrate form state from
 * a server draft that the user picked in `DraftPicker`. The file previously
 * also hosted a `useWizardDraftResume` hook that surfaced the old
 * Resume-vs-Start-Fresh modal — that modal was replaced by the two-button
 * entry pattern (New Boat + Resume Draft), so the hook is gone; only the
 * pure mapping helper remains. A `determineResumeStep` heuristic also
 * lived here but was dropped because `applyDefaultLocation` server-side
 * attaches a location to every draft on create, which made the
 * "has-location → step 3" branch always fire. Resume now always lands at
 * step 0; user can use the stepper sidebar to jump forward.
 */

/**
 * Maps a Boat API response back to a partial WizardFormData shape.
 * Only maps fields that would have been saved during draft creation.
 * Fields not present on the Boat response fall back to WIZARD_DEFAULT_VALUES.
 */
export function mapBoatToFormData(boat: Boat): Partial<WizardFormData> {
  // Trailer values are persisted to the linked inventory_trailers asset and
  // surfaced by the boat resource only under the nested trailers[] array —
  // there are no flat boat.trailer_* keys. The form binds flat trailer fields,
  // so hydrate them from the first linked trailer (flat keys still win if a
  // caller ever provides them).
  const trailer = boat.trailers?.[0];
  return {
    // Step 1: Boat & Photos
    condition: boat.condition ?? WIZARD_DEFAULT_VALUES.condition,
    manufacturer: boat.manufacturer ?? WIZARD_DEFAULT_VALUES.manufacturer,
    model: boat.model ?? WIZARD_DEFAULT_VALUES.model,
    model_year: boat.model_year ?? WIZARD_DEFAULT_VALUES.model_year,

    // Step 2: Specs & Config
    length_ft: boat.length_ft ?? WIZARD_DEFAULT_VALUES.length_ft,
    beam_in: boat.beam_in ?? WIZARD_DEFAULT_VALUES.beam_in,
    draft_in: boat.draft_in ?? WIZARD_DEFAULT_VALUES.draft_in,
    seating_capacity: boat.seating_capacity ?? WIZARD_DEFAULT_VALUES.seating_capacity,
    exterior_color: boat.exterior_color ?? WIZARD_DEFAULT_VALUES.exterior_color,
    interior_color: boat.interior_color ?? WIZARD_DEFAULT_VALUES.interior_color,
    hin_number: boat.hin_number ?? WIZARD_DEFAULT_VALUES.hin_number,
    fuel_type: boat.fuel_type ?? WIZARD_DEFAULT_VALUES.fuel_type,
    boat_types: boat.boat_types ?? WIZARD_DEFAULT_VALUES.boat_types,
    engine_type: boat.engine_type ?? WIZARD_DEFAULT_VALUES.engine_type,
    engines: boat.engines ?? WIZARD_DEFAULT_VALUES.engines,
    trailer_vin: trailer?.trailer_vin ?? WIZARD_DEFAULT_VALUES.trailer_vin,
    trailer_make: trailer?.trailer_make ?? WIZARD_DEFAULT_VALUES.trailer_make,
    trailer_model: trailer?.trailer_model ?? WIZARD_DEFAULT_VALUES.trailer_model,
    trailer_axles:
      (trailer?.trailer_axles != null ? String(trailer.trailer_axles) : undefined) ??
      WIZARD_DEFAULT_VALUES.trailer_axles,
    // #515: seed the attached trailer asset's cost for the invoice auto-calc.
    linked_trailer_cost_price:
      trailer?.cost_price != null ? Number(trailer.cost_price) : WIZARD_DEFAULT_VALUES.linked_trailer_cost_price,
    standard_equipment: boat.standard_equipment ?? WIZARD_DEFAULT_VALUES.standard_equipment,
    optional_equipment: boat.optional_equipment ?? WIZARD_DEFAULT_VALUES.optional_equipment,

    // Step 3: Pricing
    our_price: boat.our_price ?? WIZARD_DEFAULT_VALUES.our_price,
    msrp: boat.msrp ?? WIZARD_DEFAULT_VALUES.msrp,
    regular_price: boat.regular_price ?? WIZARD_DEFAULT_VALUES.regular_price,
    sale_price: boat.sale_price ?? WIZARD_DEFAULT_VALUES.sale_price,
    cost_price: boat.cost_price ?? WIZARD_DEFAULT_VALUES.cost_price,
    is_on_sale: boat.is_on_sale ?? WIZARD_DEFAULT_VALUES.is_on_sale,
    boat_cost_price: boat.boat_cost_price ?? WIZARD_DEFAULT_VALUES.boat_cost_price,
    boat_retail_price: boat.boat_retail_price ?? WIZARD_DEFAULT_VALUES.boat_retail_price,
    invoice_amount: boat.invoice_amount ?? WIZARD_DEFAULT_VALUES.invoice_amount,
    rigging: boat.rigging ?? WIZARD_DEFAULT_VALUES.rigging,
    freight: boat.freight ?? WIZARD_DEFAULT_VALUES.freight,

    // Step 4: Listing Details
    description: boat.description ?? WIZARD_DEFAULT_VALUES.description,
    short_description: boat.short_description ?? WIZARD_DEFAULT_VALUES.short_description,
    website_status: boat.website_status ?? WIZARD_DEFAULT_VALUES.website_status,
    status_id: (boat.status_id ?? WIZARD_DEFAULT_VALUES.status_id) as string,
    ribbon_id: (boat.ribbon_id ?? WIZARD_DEFAULT_VALUES.ribbon_id) as string,
    ribbon_expiry_type: (boat.ribbon_expiry_type ?? WIZARD_DEFAULT_VALUES.ribbon_expiry_type) as WizardFormData['ribbon_expiry_type'],
    ribbon_expiry_value: (boat.ribbon_expiry_value ?? WIZARD_DEFAULT_VALUES.ribbon_expiry_value) as string,
    location_ids: boat.locations?.map((loc) => loc.id) ?? WIZARD_DEFAULT_VALUES.location_ids,
    primary_location_id:
      boat.locations?.find((loc) => loc.is_primary)?.id ?? WIZARD_DEFAULT_VALUES.primary_location_id,
  };
}